/**
 * Workspace usage (GET /usage, tenant-scoped). Optional Organization tab (GET /orgs/:id/…) for OWNER/ADMIN + orgId.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { Link, useSearchParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { canAccessWorkspaceUsage, canAccessOrgBilling } from '@/utils/stepflowRoles';
import PlatformBillingPanel, { StorageGaugeDailyBadge } from '@stepflow/ui-workbench/components/PlatformBillingPanel.jsx';
import BudgetAllocationPanel from '@stepflow/ui-shared/components/BudgetAllocationPanel.jsx';
import { formatCompactNumber } from '@stepflow/ui-shared/utils/formatCompactNumber.js';
import { OrgUsagePanel } from './OrgUsagePage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

// Compact "K" formatting (1,245,238 → "1,245K") — shared house rule.
function formatInt(n) {
  return formatCompactNumber(n);
}

function formatUsd(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const v = Number(n);
  if (v < 0.01 && v > 0) return `<$0.01`;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(v);
}

function ModelTable({ rows }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No model usage in this period yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Model</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const label = row.modelId || row.modelLabel || '—';
            const tin = row.inputTokens ?? 0;
            const tout = row.outputTokens ?? 0;
            const tt = row.totalTokens ?? tin + tout;
            const cost = row.estimatedCost ?? 0;
            return (
              <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                  {row.modelLabel || '—'}
                </td>
                <td className="px-4 py-3">
                  <div
                    className="font-medium text-gray-900 truncate max-w-md"
                    title={String(label)}
                  >
                    {label}
                  </div>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(tin)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(tout)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                  {formatInt(tt)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                  {formatUsd(cost)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AgentTable({ rows, nameById }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No agent-attributed usage in this period yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Network</th>
            <th className="px-4 py-3">Agent</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
              <td className="px-4 py-3">
                <Link
                  to={`/network/${row.networkId}`}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  title={row.networkId}
                >
                  {nameById?.[row.networkId] || row.networkId}
                </Link>
              </td>
              <td className="px-4 py-3 font-medium text-gray-900">{row.agentId}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                {formatInt(row.inputTokens)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                {formatInt(row.outputTokens)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                {formatInt(row.totalTokens ?? (row.inputTokens || 0) + (row.outputTokens || 0))}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                {formatUsd(row.estimatedCost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Account-wide invoice-style lines: AI (from GET /usage byModel) + platform (from GET /usage/infra). */
export function buildAccountBillingSummary(usagePayload, infraPayload) {
  const aiRows = [];
  const byModel = usagePayload?.byModel || [];
  byModel.forEach((row, i) => {
    const cost = Number(row.estimatedCost) || 0;
    const tin = row.inputTokens ?? 0;
    const tout = row.outputTokens ?? 0;
    if (cost === 0 && tin + tout === 0) return;
    aiRows.push({
      id: `llm-${row.modelId || row.modelLabel || i}`,
      label: row.modelLabel || 'AI usage',
      quantityDisplay: `${formatInt(tin)} in / ${formatInt(tout)} out tokens`,
      amountUsd: cost,
    });
  });
  const platformRows = (infraPayload?.customerEstimates?.lines || []).map((row) => ({
    id: `plat-${row.id}`,
    label: row.label,
    quantityDisplay: row.quantityDisplay,
    amountUsd: Number(row.amountUsd) || 0,
  }));
  const aiSubtotal = aiRows.reduce((s, r) => s + r.amountUsd, 0);
  const platformSubtotal = platformRows.reduce((s, r) => s + r.amountUsd, 0);
  const totalUsd = aiSubtotal + platformSubtotal;

  // CURRENT-MONTH headline (AWS-style): backend now returns a `currentMonth`
  // block on each payload — the current calendar month ONLY, priced by the same
  // estimator as the range total. The `*Subtotal`/`totalUsd` above stay the
  // SELECTED-RANGE roll-up (shown in the detail table, labeled as a range). If a
  // rolling deploy hasn't shipped the backend yet, currentMonth is absent → the
  // headline falls back to null and the UI shows only the range (old behavior).
  const cmAi = Number(usagePayload?.currentMonth?.estimatedCost);
  const cmPlatform = (infraPayload?.currentMonth?.customerEstimates?.lines || [])
    .reduce((s, r) => s + (Number(r.amountUsd) || 0), 0);
  const hasCurrentMonth =
    usagePayload?.currentMonth != null || infraPayload?.currentMonth != null;
  const currentMonthAi = Number.isFinite(cmAi) ? cmAi : 0;
  const currentMonthPlatform = infraPayload?.currentMonth ? cmPlatform : 0;
  const currentMonthLabel =
    usagePayload?.currentMonth?.month || infraPayload?.currentMonth?.month || null;

  return {
    aiRows,
    platformRows,
    aiSubtotal,
    platformSubtotal,
    totalUsd,
    // Current-month headline (null when the backend hasn't shipped it yet).
    hasCurrentMonth,
    currentMonthLabel,
    currentMonthAi,
    currentMonthPlatform,
    currentMonthTotal: currentMonthAi + currentMonthPlatform,
    platformDisclaimer: infraPayload?.customerEstimates?.disclaimer,
    currency: infraPayload?.customerEstimates?.currency || 'USD',
  };
}

export function AccountBillingSummaryCard({
  summary,
  range,
  infraError,
  title = 'Account billing summary',
  subtitle = 'All networks combined — this is the roll-up that matches how we meter your account. Per-network pages are for detail only.',
}) {
  const hasAi = summary.aiRows.length > 0;
  const hasPlat = summary.platformRows.length > 0;
  const hasAny = hasAi || hasPlat;

  return (
    <div className="mb-8 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-xs text-gray-500 leading-relaxed max-w-3xl">{subtitle}</p>
        {summary.hasCurrentMonth ? (
          <div className="mt-3 flex flex-wrap items-end justify-between gap-3 rounded-lg border border-emerald-100 bg-emerald-50/60 px-4 py-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
                This month{summary.currentMonthLabel ? ` · ${summary.currentMonthLabel}` : ''}
              </p>
              <p className="mt-0.5 text-[11px] text-emerald-700/80">
                AI {formatUsd(summary.currentMonthAi)} · Platform {formatUsd(summary.currentMonthPlatform)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold tabular-nums text-emerald-950">
                {formatUsd(summary.currentMonthTotal)}
              </p>
              <p className="text-[11px] text-emerald-700/80">{summary.currency} · so far this month</p>
            </div>
          </div>
        ) : null}
        {range?.from && range?.to ? (
          <p className="mt-2 text-xs font-medium text-gray-600">
            Detail below covers the selected range: {range.from} → {range.to} · {summary.currency}
          </p>
        ) : null}
      </div>

      {infraError ? (
        <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Platform charge lines could not be loaded ({infraError}). The estimated total below excludes platform until this
          succeeds.
        </div>
      ) : null}

      <div className="px-2 py-2 sm:px-0">
        {!hasAny ? (
          <div className="px-5 py-12 text-center text-sm text-gray-500">
            No billable usage in this range yet.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                <th className="px-5 py-3">Line item</th>
                <th className="px-5 py-3">Quantity</th>
                <th className="px-5 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {hasAi ? (
                <>
                  <tr className="bg-slate-50/90">
                    <td colSpan={3} className="px-5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                      AI &amp; agents
                    </td>
                  </tr>
                  {summary.aiRows.map((row) => (
                    <tr key={row.id} className="border-b border-gray-50 hover:bg-slate-50/40">
                      <td className="px-5 py-2.5 font-medium text-gray-900">{row.label}</td>
                      <td className="px-5 py-2.5 text-xs text-gray-600 tabular-nums">{row.quantityDisplay}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums font-medium text-gray-900">
                        {formatUsd(row.amountUsd)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b border-gray-100 bg-white">
                    <td colSpan={2} className="px-5 py-2 text-xs font-medium text-gray-600 text-right">
                      Subtotal (AI)
                    </td>
                    <td className="px-5 py-2 text-right text-sm font-semibold tabular-nums text-gray-900">
                      {formatUsd(summary.aiSubtotal)}
                    </td>
                  </tr>
                </>
              ) : null}

              {hasPlat ? (
                <>
                  <tr className="bg-slate-50/90">
                    <td colSpan={3} className="px-5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                      Platform &amp; storage
                    </td>
                  </tr>
                  {summary.platformRows.map((row) => (
                    <tr key={row.id} className="border-b border-gray-50 hover:bg-slate-50/40">
                      <td className="px-5 py-2.5 font-medium text-gray-900">{row.label}</td>
                      <td className="px-5 py-2.5 text-xs text-gray-600 tabular-nums">{row.quantityDisplay}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums font-medium text-gray-900">
                        {formatUsd(row.amountUsd)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b border-gray-100 bg-white">
                    <td colSpan={2} className="px-5 py-2 text-xs font-medium text-gray-600 text-right">
                      Subtotal (platform)
                    </td>
                    <td className="px-5 py-2 text-right text-sm font-semibold tabular-nums text-gray-900">
                      {formatUsd(summary.platformSubtotal)}
                    </td>
                  </tr>
                </>
              ) : !infraError ? (
                <tr>
                  <td colSpan={3} className="px-5 py-4 text-xs text-gray-500">
                    No platform charge lines for this range (estimates unavailable or zero).
                  </td>
                </tr>
              ) : null}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td colSpan={2} className="px-5 py-4 text-sm font-semibold text-gray-700">
                  Selected-range total ({summary.currency})
                  {range?.from && range?.to ? (
                    <span className="ml-1 font-normal text-gray-400">· {range.from} → {range.to}</span>
                  ) : null}
                </td>
                <td className="px-5 py-4 text-right text-lg font-semibold tabular-nums text-gray-900">
                  {formatUsd(summary.totalUsd)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      <div className="border-t border-gray-100 bg-gray-50/80 px-5 py-3 text-[11px] text-gray-500 leading-relaxed">
        {summary.platformDisclaimer ||
          'Indicative totals from metering — not a tax invoice. Final amounts may differ; payment and receipts are handled separately when you enable billing.'}
      </div>
    </div>
  );
}

function NetworkTable({ rows }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No networks yet.
      </div>
    );
  }
  const withUsage = rows.filter((r) => (r.totals?.estimatedCost || 0) > 0);
  const display = withUsage.length ? withUsage : rows;
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Network</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {display.map((row) => (
            <tr key={row.networkId} className="border-b border-gray-50 hover:bg-slate-50/60">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">{row.name || row.networkId}</div>
                <div className="text-xs text-gray-400 font-mono truncate max-w-xs">{row.networkId}</div>
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                {formatInt(row.totals?.inputTokens)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                {formatInt(row.totals?.outputTokens)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                {formatUsd(row.totals?.estimatedCost)}
              </td>
              <td className="px-4 py-3 text-right">
                <Link
                  to={`/network/${row.networkId}`}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Open
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AccountUsagePage() {
  const { user, workspaceRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = user?.orgId || '';
  const workspaceBillingAllowed = canAccessWorkspaceUsage(workspaceRole);
  const showOrgScope = canAccessOrgBilling(workspaceRole, orgId);
  const scopeIsOrg = searchParams.get('tab') === 'org';

  useEffect(() => {
    if (scopeIsOrg && !showOrgScope) {
      setSearchParams({}, { replace: true });
    }
  }, [scopeIsOrg, showOrgScope, setSearchParams]);

  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infraError, setInfraError] = useState(null);
  const [data, setData] = useState(null);
  const [infraData, setInfraData] = useState(null);

  const fetchAccountUsage = useCallback(async () => {
    if (!workspaceBillingAllowed) return;
    setLoading(true);
    setError(null);
    setInfraError(null);
    const idToken = getRestApiBearerToken();
    const q = new URLSearchParams({ months: String(months) });
    const headers = {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
    try {
      const [usageRes, infraRes] = await Promise.all([
        fetch(`${API_BASE_URL}/usage?${q}`, { headers }),
        fetch(`${API_BASE_URL}/usage/infra?${q}`, { headers }),
      ]);
      const usageBody = await usageRes.json().catch(() => ({}));
      const infraBody = await infraRes.json().catch(() => ({}));
      if (!usageRes.ok) {
        throw new Error(usageBody.error || usageBody.message || usageRes.statusText);
      }
      setData(usageBody);
      if (!infraRes.ok) {
        setInfraData(null);
        setInfraError(infraBody.error || infraBody.message || infraRes.statusText);
      } else {
        setInfraData(infraBody);
        setInfraError(null);
      }
    } catch (e) {
      setError(e.message || 'Failed to load usage');
      setData(null);
      setInfraData(null);
    } finally {
      setLoading(false);
    }
  }, [months, user?.idToken, workspaceBillingAllowed]);

  useEffect(() => {
    if (!workspaceBillingAllowed) return;
    fetchAccountUsage();
  }, [fetchAccountUsage, workspaceBillingAllowed]);

  const totals = data?.totals;
  const range = data?.range;
  const byModel = data?.byModel || [];
  const byAgent = data?.byAgent || [];
  const monthly = data?.monthly || [];
  const byNetwork = data?.byNetwork || [];

  const networkNameById = useMemo(() => {
    const m = {};
    byNetwork.forEach((n) => {
      if (n.networkId) m[n.networkId] = n.name || n.networkId;
    });
    return m;
  }, [byNetwork]);

  const maxMonthCost = useMemo(() => {
    let m = 0;
    monthly.forEach((row) => {
      const c = Number(row.estimatedCost) || 0;
      if (c > m) m = c;
    });
    return m || 1;
  }, [monthly]);

  const billingSummary = useMemo(
    () => buildAccountBillingSummary(data, infraData),
    [data, infraData]
  );

  if (!workspaceBillingAllowed) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center bg-slate-50 px-6 py-16">
        <p className="text-center text-gray-600">
          Usage and billing require a workspace session with tenant context.
        </p>
        <Link
          to="/network/new"
          className="mt-6 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          Back to workbench
        </Link>
      </div>
    );
  }

  const workspacePanel = (
    <>
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-gray-900">Workspace usage &amp; billing</h1>
              <p className="mt-1 text-sm text-gray-500">
                Tenant-scoped: only networks in this workspace. Open a network for per-network detail.
                {range?.from && range?.to ? (
                  <span className="text-gray-400">
                    {' '}
                    · {range.from} → {range.to}
                  </span>
                ) : null}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="acct-usage-months"
                className="text-xs font-medium uppercase tracking-wide text-gray-500"
              >
                Range
              </label>
              <select
                id="acct-usage-months"
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
              >
                <option value={3}>Last 3 months</option>
                <option value={6}>Last 6 months</option>
                <option value={12}>Last 12 months</option>
              </select>
            </div>
          </div>
        </motion.div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        ) : (
          <Tab.Group>
            <Tab.List className="mb-6 flex flex-wrap w-fit gap-1 rounded-lg bg-slate-200/70 p-1">
              {[
                {
                  label: 'Billing',
                  title: 'Workspace line items: AI usage plus platform & storage (this tenant’s networks)',
                },
                { label: 'Networks', title: 'Allocate this workspace’s points budget across its networks' },
                { label: 'LLM tokens', title: 'Aggregated LLM cost and tokens (detail)' },
                {
                  label: 'Platform',
                  title:
                    'All networks: vector + app + buffer metering — detail; per-network Usage is informational',
                },
              ].map(({ label, title }) => (
                <Tab
                  key={label}
                  title={title}
                  className={({ selected }) =>
                    `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  {label}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <AccountBillingSummaryCard
                  summary={billingSummary}
                  range={range}
                  infraError={infraError}
                />
              </Tab.Panel>

              <Tab.Panel>
                {/* Allocate this tenant's points budget across its networks
                    (USAGE-AND-BUDGET.md §7b). */}
                <BudgetAllocationPanel
                  scope="tenant"
                  id={user?.tenantId || ''}
                  childNoun="network"
                  title="Budget by network"
                />
              </Tab.Panel>

              <Tab.Panel>
                <>
                  <p className="mb-4 text-xs text-gray-500 max-w-2xl">
                    Breakdown for your review. Account charges are summarized on the <strong>Billing</strong> tab.
                  </p>
                  <h2 className="mb-6 text-base font-semibold text-gray-900">LLM tokens &amp; cost (this workspace)</h2>
            <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                { label: 'Est. cost', value: formatUsd(totals?.estimatedCost), sub: 'USD · selected range' },
                { label: 'Total tokens', value: formatInt(totals?.totalTokens), sub: 'input + output · range' },
                { label: 'Input tokens', value: formatInt(totals?.inputTokens), sub: 'prompt · range' },
                { label: 'Output tokens', value: formatInt(totals?.outputTokens), sub: 'completion · range' }
              ].map((k) => (
                <div
                  key={k.label}
                  className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                >
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{k.label}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{k.value}</p>
                  <p className="mt-0.5 text-xs text-gray-400">{k.sub}</p>
                </div>
              ))}
            </div>

            {monthly.length > 0 && (
              <div className="mb-10 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-sm font-semibold text-gray-800">Cost by month</h2>
                <div className="flex h-40 items-end gap-1 pl-1 sm:gap-2">
                  {monthly.map((row) => {
                    const cost = Number(row.estimatedCost) || 0;
                    const h = maxMonthCost > 0 ? Math.max(8, (cost / maxMonthCost) * 100) : 8;
                    return (
                      <div
                        key={row.month}
                        className="group flex min-w-0 flex-1 h-full flex-col items-center justify-end"
                      >
                        <div
                          className="mx-auto w-full max-w-[48px] rounded-t-md bg-gradient-to-t from-blue-600 to-blue-400 opacity-90 transition-opacity group-hover:opacity-100"
                          style={{ height: `${h}%` }}
                          title={`${row.month}: ${formatUsd(cost)}`}
                        />
                        <span className="mt-2 w-full truncate text-center text-[10px] text-gray-500 sm:text-xs">
                          {row.month.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-center text-xs text-gray-400">
                  Bar height is relative to the highest-cost month in this range.
                </p>
              </div>
            )}

            <Tab.Group>
              <Tab.List className="mb-4 flex w-fit gap-1 rounded-lg bg-gray-100/80 p-1">
                {['By network', 'By model', 'By agent'].map((label) => (
                  <Tab
                    key={label}
                    className={({ selected }) =>
                      `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`
                    }
                  >
                    {label}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                <Tab.Panel>
                  <NetworkTable rows={byNetwork} />
                </Tab.Panel>
                <Tab.Panel>
                  <ModelTable rows={byModel} />
                </Tab.Panel>
                <Tab.Panel>
                  <AgentTable rows={byAgent} nameById={networkNameById} />
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
                </>
              </Tab.Panel>

              <Tab.Panel>
                <div className="flex flex-wrap items-center gap-2 gap-y-1 mb-2">
                  <h2 className="text-base font-semibold text-gray-900">Platform &amp; storage (all networks)</h2>
                  <StorageGaugeDailyBadge />
                </div>
                <p className="text-xs text-gray-500 mb-4 max-w-3xl">
                  Detail view across your networks. Estimated platform charges for the account are on the{' '}
                  <strong>Billing</strong> tab. Byte totals in Vector / General / Buffer refresh on a{' '}
                  <strong className="font-medium text-gray-700">~daily</strong> schedule; compute counters update as usage
                  is recorded (typically within minutes).
                </p>
                <PlatformBillingPanel
                  accountMode
                  months={months}
                  onMonthsChange={setMonths}
                  suppressCustomerEstimates
                />
                <p className="mt-8 text-xs text-gray-500 max-w-2xl">
                  Per-network live pipeline charts sit under each network’s <strong>Usage</strong> →{' '}
                  <strong>Vector DB (managed)</strong> (together with vector-index metering).
                </p>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        )}
    </>
  );

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-6xl px-6 py-8 pb-12">
        {showOrgScope ? (
          <Tab.Group
            selectedIndex={scopeIsOrg ? 1 : 0}
            onChange={(idx) => {
              if (idx === 1) setSearchParams({ tab: 'org' }, { replace: true });
              else setSearchParams({}, { replace: true });
            }}
          >
            <Tab.List className="mb-6 flex w-fit flex-wrap gap-1 rounded-lg bg-indigo-100/70 p-1">
              <Tab
                title="Usage for this workspace (tenant) only"
                className={({ selected }) =>
                  `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`
                }
              >
                Workspace
              </Tab>
              <Tab
                title="All tenant workspaces under the payer organization"
                className={({ selected }) =>
                  `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`
                }
              >
                Organization
              </Tab>
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel className="outline-none">{workspacePanel}</Tab.Panel>
              <Tab.Panel className="outline-none">
                <OrgUsagePanel />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        ) : (
          workspacePanel
        )}
      </div>
    </div>
  );
}
